import React, {
  useCallback, useRef, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { includes, min, size } from 'lodash/fp';
import { Portal } from '@material-ui/core';
import { dragIcon } from '../assets';

const S = {
  Wrapper: styled.div`
    position: absolute;
    top: 0;
    left: 0;
    z-index: ${({ active }) => (active ? 100 : -100)};
    width: 100%;
    height: 100%;
    ${({ cursor }) => cursor === 'DRAG' && css`
      cursor: url(${dragIcon}) 8 17, auto;
    `}
  `,
  Rectangle: styled.div.attrs(({
    x, y, width, height,
  }) => ({
    style: {
      transform: `translate(${x}px, ${y}px)`,
      width: `${width}px`,
      height: `${height}px`,
    },
  }))`
    position: absolute;
    top: 0;
    left: 0;
    border: 2px solid ${({ theme }) => theme.palette.status.note.main};
    border-radius: 2px;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.24);
    cursor: url(${dragIcon}) 8 17 , auto;
  `,
  QuestionNumber: styled.div`
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(calc(-100% - 2px), calc(-100% - 2px));
    padding: 4px 6px;
    border-radius: 12px 12px 0 12px;
    color: ${({ theme }) => theme.palette.primary.contrastText};
    background-color: ${({ theme }) => theme.palette.status.note.main};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 700;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.32);
  `,
};

const QuestionField = ({
  assigned,
  bindHover,
  setAssigned,
  canvasWrapper,
  className,
  name,
  form,
  remove,
  replace,
  step,
  type,
  viewport,
  setVisible,
}) => {
  const [dragging, setDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [rect, setRect] = useState({
    width: 0, height: 0, x: 0, y: 0,
  });

  const active = useMemo(() => step === 2 && type !== 'SIGNATURE', [step, type]);
  const cursor = useMemo(() => {
    if (step === 2 && type !== 'SIGNATURE') {
      return 'DRAG';
    }
    return null;
  }, [step, type]);

  const rectangle = useRef(null);

  const handlePointerDown = useCallback((event) => {
    if (step === 2 && assigned) {
      remove(size(form.values.questions) - 1);
    }

    setDragging(true);

    setAssigned(false);

    setVisible(true);

    setStartPosition({ x: event.pageX, y: event.pageY });
  }, [assigned, form.values.questions, remove, setAssigned, setVisible, step]);

  const handlePointerMove = useCallback((event) => {
    if (dragging) {
      setRect({
        width: Math.abs(event.pageX - startPosition.x),
        height: Math.abs(event.pageY - startPosition.y),
        x: min([event.pageX, startPosition.x]),
        y: min([event.pageY, startPosition.y]),
      });
    }
  }, [dragging, startPosition.x, startPosition.y]);

  const handlePointerUp = useCallback(() => {
    setDragging(false);

    setRect({
      width: 0, height: 0, x: 0, y: 0,
    });

    const canvasWrapperRect = canvasWrapper.current.getBoundingClientRect();
    const viewportRect = viewport.current.getBoundingClientRect();
    const rectangleRect = rectangle.current.getBoundingClientRect();

    const relativeX = (rectangleRect.left - viewportRect.left + viewport.current.scrollLeft);
    const relativeY = (rectangleRect.top - viewportRect.top + viewport.current.scrollTop);

    const x = (relativeX / canvasWrapperRect.width) * 100;
    const y = (relativeY / canvasWrapperRect.height) * 100;

    const width = (rectangleRect.width / canvasWrapperRect.width) * 100;
    const height = (rectangleRect.height / canvasWrapperRect.height) * 100;

    if (
      (x >= 0 && x <= 100)
      && (y >= 0 && y <= 100)
      && (rectangleRect.width >= 12 && rectangleRect.height >= 12)
    ) {
      setAssigned(true);

      const isSelectionType = includes(type, ['SINGLE_SELECTION', 'MULTIPLE_SELECTION']);

      replace(size(form.values[name]), {
        number: size(form.values[name]) + 1,
        content: `질문 ${size(form.values[name]) + 1}`,
        position: { x, y },
        required: false,
        size: { width, height },
        type,
        options: isSelectionType ? [] : null,
        field: !isSelectionType ? { width: 20 } : null,
      });
    }
  }, [canvasWrapper, viewport, setAssigned, type, replace, form.values, name]);

  return (
    <S.Wrapper
      className={className}
      active={active}
      cursor={cursor}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <Portal container={document.body}>
        <S.Rectangle ref={rectangle} {...rect} {...bindHover}>
          {dragging && (
            <S.QuestionNumber>
              Q
              {size(form.values[name]) + 1}
            </S.QuestionNumber>
          )}
        </S.Rectangle>
      </Portal>
    </S.Wrapper>
  );
};

QuestionField.propTypes = {
  assigned: PropTypes.bool.isRequired,
  bindHover: PropTypes.shape({
    onMouseEnter: PropTypes.func.isRequired,
    onMouseLeave: PropTypes.func.isRequired,
  }).isRequired,
  setAssigned: PropTypes.func.isRequired,
  canvasWrapper: PropTypes.shape({
    current: PropTypes.instanceOf(HTMLElement),
  }).isRequired,
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  form: PropTypes.shape({
    values: PropTypes.shape({
      questions: PropTypes.arrayOf(PropTypes.shape({
        number: PropTypes.number.isRequired,
        position: PropTypes.shape({
          x: PropTypes.number.isRequired,
          y: PropTypes.number.isRequired,
        }),
        required: PropTypes.bool.isRequired,
        size: PropTypes.shape({
          width: PropTypes.number.isRequired,
          height: PropTypes.number.isRequired,
        }),
        type: PropTypes.oneOf([
          'SINGLE_SELECTION',
          'MULTIPLE_SELECTION',
          'DESCRIPTIVE',
          'SIGNATURE',
          // XXX: 하위 호환성 유지를 위한 필드. 추후 제거 예정
          'SINGLE_CHOICE',
          'MULTIPLE_CHOICE',
          'SUBJECTIVE',
        ]),
      })),
    }).isRequired,
  }).isRequired,
  remove: PropTypes.func.isRequired,
  replace: PropTypes.func.isRequired,
  step: PropTypes.number.isRequired,
  type: PropTypes.oneOf([
    'SINGLE_SELECTION',
    'MULTIPLE_SELECTION',
    'DESCRIPTIVE',
    'SIGNATURE',
  ]),
  viewport: PropTypes.shape({
    current: PropTypes.instanceOf(HTMLElement),
  }).isRequired,
  setVisible: PropTypes.func.isRequired,
};

QuestionField.defaultProps = {
  className: undefined,
  type: null,
};

export default QuestionField;
